import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import DailyIframe from "@daily-co/daily-js";
import { useSnackbar } from "notistack";
import {
  create_test_room_api, delete_test_room_api, preview_join_window_api,
} from "../../../DAL/SchoolPortal/Tutors";
import {
  border, textPrimary, textSecondary, textMuted, accent, font,
  success, warning, secondaryButtonSx, filledButtonSx, ACTION_COLORS,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * /school/teaching/video-test — the call, without a lesson.
 *
 * WHY THIS PAGE EXISTS
 * Every route into the video ran through a real booking: a confirmed lesson,
 * at a real time, inside a window that opens minutes before it. So the only
 * way to look at the call UI was to book a session with a colleague and wait
 * for the clock — which means the camera, the microphone, the recording
 * banner, the consent copy and the layout had been seen by nobody. That is
 * the one screen where a fault is most expensive and least recoverable:
 * nobody debugs a black rectangle gracefully while a student watches.
 *
 * IT JOINS A REAL ROOM. A mock proves the mock works. This mints an actual
 * private room and an actual signed token through the same makeRoom() and
 * createMeetingToken() a lesson uses, so what you see here is what will run.
 *
 * THE WINDOW IS SIMULATED, NOT WAITED FOR. The join window is the part most
 * likely to be wrong and the hardest to check, because checking it honestly
 * means sitting until T-6. The table asks the server what it WOULD say at
 * each moment, from the same function the video endpoint enforces with — so
 * a change to those constants shows up here immediately rather than the next
 * time somebody is early for a lesson.
 *
 * BOTH SIDES, BECAUSE THEY DIFFER. The instructor's window opens at T-30 and
 * their token may start a recording; the student's opens at T-5 and theirs
 * may not. Joining as a student is the only way to see what a student sees.
 *
 * AND BOTH SIDES ARE ONE ROOM. The room name is stable per person, so the two
 * tabs meet — which is the only thing worth testing here. The first version
 * put a timestamp in the name and built a fresh room per click, so instructor
 * and student sat alone in separate calls looking at exactly what a working
 * two-person call looks like from one side.
 */

const H = { fontFamily: font };
const label = {
  ...H, fontSize: "10px", fontWeight: 800, letterSpacing: "0.07em",
  textTransform: "uppercase", color: textMuted,
};

/** The moments worth checking, on both sides of every boundary. */
const PROBES = [31, 29, 6, 4, 0, -31];

export default function VideoTest() {
  const { enqueueSnackbar } = useSnackbar();
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const [room, setRoom] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [probes, setProbes] = useState([]);

  useEffect(() => {
    Promise.all(PROBES.flatMap((m) => ["instructor", "student"].map(
      (as) => preview_join_window_api(m, as).then((r) => (r?.code === 200 ? { m, as, ...r } : null))
    ))).then((rows) => setProbes(rows.filter(Boolean)));
  }, []);

  /**
   * Tearing the frame down is not optional cleanup.
   *
   * A destroyed React tree with a live DailyIframe behind it keeps the camera
   * light on, which is the single most alarming way for a test tool to fail.
   */
  const teardown = useCallback(() => {
    if (frameRef.current) {
      try { frameRef.current.destroy(); } catch (e) { /* already gone */ }
      frameRef.current = null;
    }
  }, []);

  useEffect(() => teardown, [teardown]);

  const start = async (as) => {
    setBusy(true);
    setError("");
    teardown();
    try {
      const r = await create_test_room_api(as);
      if (r?.code !== 200) { setError(r?.message || "Could not open a room"); return; }
      setRoom({ ...r, as });
      const frame = DailyIframe.createFrame(containerRef.current, {
        showLeaveButton: true,
        iframeStyle: { width: "100%", height: "100%", border: "0" },
      });
      frameRef.current = frame;
      frame.on("left-meeting", () => { teardown(); setRoom(null); });
      await frame.join({ url: r.room.url, token: r.token });
    } catch (e) {
      setError(e.message || "Could not join");
    } finally { setBusy(false); }
  };

  const stop = async () => {
    teardown();
    if (room?.room?.name) {
      const r = await delete_test_room_api(room.room.name);
      enqueueSnackbar(r?.message || "Room closed", { variant: r?.code === 200 ? "success" : "error" });
    }
    setRoom(null);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1400, mx: "auto" }}>
      <Typography sx={{ ...H, fontSize: "20px", fontWeight: 800, color: textPrimary, letterSpacing: "-0.2px" }}>
        Video test bench
      </Typography>
      <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, mt: 0.2, mb: 2, maxWidth: 640, lineHeight: 1.6 }}>
        A real Daily room and a real signed token, minted through the same code a lesson uses — with no
        booking behind it and no clock to wait for. Rooms expire on their own after an hour whether or not
        you close them.
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1fr) 340px" }, gap: 2, alignItems: "start" }}>
        <Box>
          <Box sx={{ display: "flex", gap: 0.8, mb: 1.5, flexWrap: "wrap" }}>
            <Button disabled={busy} onClick={() => start("instructor")} sx={filledButtonSx(ACTION_COLORS.join)}>
              Join as instructor
            </Button>
            <Button disabled={busy} onClick={() => start("student")} sx={filledButtonSx(ACTION_COLORS.handover)}>
              Join as student
            </Button>
            {room && (
              <Button onClick={stop} sx={{ ...secondaryButtonSx, minHeight: 0, py: 0.55, px: 1.4, fontSize: "12.5px" }}>
                Leave &amp; delete room
              </Button>
            )}
          </Box>

          {error && (
            <Typography sx={{ ...H, fontSize: "12.5px", color: warning, mb: 1.5 }}>{error}</Typography>
          )}

          <Box
            ref={containerRef}
            sx={{
              height: { xs: 380, md: 520 }, borderRadius: "12px", overflow: "hidden",
              border: `1px solid ${border}`, backgroundColor: "#000",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            {busy ? <CircularProgress size={22} sx={{ color: "#fff" }} />
              : !room && (
                <Typography sx={{ ...H, fontSize: "13px", color: "#8a8580" }}>
                  Pick a side above. The two are not the same call.
                </Typography>
              )}
          </Box>

          {room && (
            <Box sx={{ mt: 1.25, p: 1.4, border: `1px solid ${border}`, borderRadius: "10px", backgroundColor: "#fff" }}>
              <Typography sx={{ ...label, mb: 0.8 }}>This room</Typography>
              <Typography sx={{ ...H, fontSize: "11.5px", color: textSecondary, mb: 0.8, lineHeight: 1.55 }}>
                Open this page in a second window and pick the other side — both land here. To pull a
                colleague in, send them the room name below; theirs would otherwise be their own.
              </Typography>
              {[
                ["Joined as", room.joinedAs],
                // The one asymmetry people trip over: recording control lives on
                // the instructor's TOKEN, not on the room, so a student cannot
                // start one however the room is configured.
                ["May record", room.mayRecord ? "yes — control is on the token, not the room" : "no — a student's token cannot start one"],
                ["Room", room.shareRoom || room.room.name],
                ["Expires", new Date(room.expiresAt).toLocaleTimeString()],
              ].map(([k, v]) => (
                <Box key={k} sx={{ display: "flex", gap: 1, py: 0.3 }}>
                  <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, minWidth: 92 }}>{k}</Typography>
                  <Typography sx={{ ...H, fontSize: "12.5px", color: textPrimary, wordBreak: "break-all" }}>{v}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ p: 1.4, border: `1px solid ${border}`, borderRadius: "10px", backgroundColor: "#fff" }}>
          <Typography sx={{ ...label, mb: 0.3 }}>When each side can get in</Typography>
          <Typography sx={{ ...H, fontSize: "11.5px", color: textSecondary, mb: 1.2, lineHeight: 1.55 }}>
            Asked of the server, from the same function the video endpoint enforces with — so this moves
            the moment those constants do.
          </Typography>
          {!probes.length ? <CircularProgress size={16} sx={{ color: accent }} /> : (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: "58px 1fr 1fr", gap: 0.5, pb: 0.5, borderBottom: `1px solid ${border}` }}>
                <Typography sx={label} />
                <Typography sx={label}>Instructor</Typography>
                <Typography sx={label}>Student</Typography>
              </Box>
              {PROBES.map((m) => {
                const cell = (as) => probes.find((p) => p.m === m && p.as === as);
                return (
                  <Box key={m} sx={{ display: "grid", gridTemplateColumns: "58px 1fr 1fr", gap: 0.5, py: 0.55, borderBottom: `1px solid ${border}` }}>
                    <Typography sx={{ ...H, fontSize: "11.5px", color: textSecondary, fontVariantNumeric: "tabular-nums" }}>
                      {m >= 0 ? `T-${m}` : `T+${-m}`}
                    </Typography>
                    {["instructor", "student"].map((as) => {
                      const c = cell(as);
                      const open = c?.state === "open";
                      return (
                        <Typography key={as} sx={{
                          ...H, fontSize: "11.5px", fontWeight: open ? 700 : 400,
                          color: open ? success : textMuted,
                        }}>
                          {c ? (open ? "open" : c.state.replace("_", " ")) : "—"}
                        </Typography>
                      );
                    })}
                  </Box>
                );
              })}
              <Typography sx={{ ...H, fontSize: "11px", color: textMuted, mt: 1, lineHeight: 1.55 }}>
                T is the scheduled start. The instructor is let in earlier on purpose — a student arriving
                to find someone already there is the difference between a lesson and a conference call.
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
